import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useTranslation } from "react-i18next"

import ProtectedLayout from "layouts/Protected"
import * as PartnerApi from "features/Partner/api/PartnerApi"
import { useAuth, useAlert, useTheme } from "hooks"
import TeamManagementIcon from "components/Elements/Icons/TeamManagement"
import { fetchPartner } from "./hooks/fetchPartner"
import { prepareDataToPut } from "./hooks/prepareDataToPut"
import { BC_OPERATOR, SELLER_PERMISSION } from "utils/constants"
import {
	MandateForm,
	AdditionalInformationForm,
	ManagerForm,
	AddressForm,
	StructureForm,
	BilingForm,
} from "./Partials"
import { ErrorResponse } from "models"
import { DEFAULT_SIZE } from "utils/icons"
import { useLocation } from "react-router-dom"

export const PartnerInfo = () => {
	const [partner, setPartner] = React.useState(null)
	const [updatedPartner, setUpdatedPartner] = React.useState(null)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)
	const [editForm, setEditForm] = React.useState(false)
	const { entity, isConnectedFromSupport, fetchEntity, user } = useAuth()
	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentTheme } = useTheme()
	const location = useLocation()

	React.useEffect(() => {
		if (entity) fetchPartner(entity, setPartner)
	}, [entity])

	React.useEffect(() => {
		if (location?.state?.navigation) {
			setTimeout(() => {
				document.getElementById(location?.state?.navigation) &&
					document
						.getElementById(location?.state?.navigation)
						?.scrollIntoView({ behavior: "smooth" })
			}, 200)
		}
	}, [location])

	const isBCPartner = entity?.data_operators?.find(
		(oneOperator) => oneOperator.operator === BC_OPERATOR
	)
	const isBCCurrentTheme = currentTheme?.id_api === BC_OPERATOR
	const isProductionEnvironment = process.env.REACT_APP_MODE === "production" // Ne pas afficher le bloc Mandat en production sans le raccordement avec sage
	const isSellerPermission = user?.permission === SELLER_PERMISSION

	const isEditAllowed = !isSellerPermission // Si l'utilisateur n'est pas un vendeur, on affiche les boutons "Modifier" / "Annuler"

	const editPartner = async () => {
		try {
			// hide error
			setShowLocalErrors(false)

			const updatedPartnerObject = prepareDataToPut({
				isUpdate: true,
				partner: { ...partner, ...updatedPartner },
				isPartner: true,
			})

			const { getError } = await PartnerApi.updateEntity(updatedPartnerObject)
			if (getError()) throw getError()
			alert("success", t("edit_success", { ns: "partner" }))
			setEditForm(false)
		} catch (error) {
			console.error(error)
			const erroMsg = (error as ErrorResponse)?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
		} finally {
			fetchEntity(entity.entity_id)
		}
	}

	const componentCommonProps = {
		partner: partner,
		editCallback: editPartner,
		updatedPartner: updatedPartner,
		setUpdatedPartner: setUpdatedPartner,
		errors: errors,
		setErrors: setErrors,
		showLocalErrors: showLocalErrors,
		setShowLocalErrors: setShowLocalErrors,
	}

	const listTags = [
		{
			label: t("partner_form.legalInfo", { ns: "partner" }),
			id: "structure",
			visibilty: true,
			editable: false,
		},
		{
			label: t("partner_form.contactDetails", { ns: "partner" }),
			id: "address",
			visibilty: true,
			editable: false,
		},
		{
			label: t("partner_form.manager", { ns: "partner" }),
			id: "manager",
			visibilty: true,
			editable: false,
		},
		{
			label: t("partner_form.moreInfo", { ns: "partner" }),
			id: "additional",
			visibilty: true,
			editable: true,
		},
		{
			label: t("partner_form.billing", { ns: "partner" }),
			id: "billing",
			visibilty: isBCPartner && isBCCurrentTheme,
			editable: true,
		},
	]

	return (
		<ProtectedLayout className="py-5">
			{!partner && (
				<div className="flex justify-center items-center py-12">
					<div className="c-spinner"></div>
				</div>
			)}

			{partner && (
				<>
					<div className="l-container">
						<div
							className={clsx("c-section-title mb-5 ", {
								"px-8": !isMobileOnly,
								"px-6": isMobileOnly,
							})}
						>
							<div className="c-section-title__content justify-between">
								<div className="flex items-center justify-start w-full">
									<TeamManagementIcon size={DEFAULT_SIZE} className="" />
									<h1 className="c-section-title__title c-h2 text-primary ml-4">
										{t("store.title", { ns: "partner" })}
									</h1>
								</div>
								<div className="flex gap-2">
									{isEditAllowed && (
										<div className={clsx({ "flex flex-col": isMobileOnly })}>
											{editForm ? (
												<>
													<button
														onClick={() => setEditForm(false)}
														className={clsx(
															"c-btn c-btn--red c-btn--s px-7",
															{ "mt-2": isMobileOnly }
														)}
													>
														{t("global.cancel")}
													</button>
													<button
														onClick={editPartner}
														className={clsx(
															"c-btn c-btn--primary c-btn--m px-12",
															{ "mt-2": isMobileOnly }
														)}
													>
														{t("global.save")}
													</button>
												</>
											) : (
												<>
													<button
														type="submit"
														className={clsx(
															"c-btn c-btn--primary c-btn--m px-12",
															{ "mt-2": isMobileOnly }
														)}
														onClick={() => setEditForm(true)}
													>
														{t("global.update")}
													</button>
												</>
											)}
										</div>
									)}
								</div>
							</div>
						</div>

						<div
							className={clsx("flex mb-4 px-8", {
								"justify-between": editForm,
								"justify-end": !editForm,
								"flex-col mr-8": isMobileOnly,
								"mx-8": !isMobileOnly,
							})}
						>
							{editForm && (
								<p className={clsx("text-danger", { "mb-2": isMobileOnly })}>
									{t("store.information", { ns: "partner" })}
								</p>
							)}
							<div
								className={clsx("text-xs", {
									"text-right ": !isMobileOnly,
									"text-center": isMobileOnly,
								})}
							>
								{t("store.declarator_number", { ns: "partner" })} :{" "}
								{partner.entity_id}
							</div>
						</div>

						<div
							className={clsx("c-card p-8", {
								"mx-8": !isMobileOnly,
								"mx-6 w-3/4": isMobileOnly,
							})}
						>
							<div
								className={clsx("flex gap-4 flex-wrap p-4 box-border ", {
									"flex-col": isMobileOnly,
								})}
							>
								{listTags
									.filter((element) => element.visibilty)
									.map((element, key) => {
										return (
											<button
												key={key}
												onClick={() =>
													document
														.getElementById(element.id)
														.scrollIntoView({ behavior: "smooth" })
												}
												className={clsx({
													"text-grey70": editForm && !element.editable,
												})}
											>
												{element.label}
											</button>
										)
									})}
							</div>

							<StructureForm
								{...componentCommonProps}
								isEdit={false}
								isPartnerAlreadyRegistered={true}
							/>

							<hr className="h-1px bg-neutral400" />

							<AddressForm
								{...componentCommonProps}
								isEdit={false}
								isViewMode={true}
							/>

							<hr className="h-1px bg-neutral400" />

							<ManagerForm
								{...componentCommonProps}
								isEdit={false}
								isViewMode={true}
								refresh={fetchEntity}
							/>

							<hr className="h-1px bg-neutral400" />

							<AdditionalInformationForm
								{...componentCommonProps}
								isEdit={editForm}
								isViewMode={isSellerPermission}
								refresh={fetchEntity}
								isAdminEditing={false}
							/>
						</div>
					</div>

					<div
						className={clsx("c-card px-8 mt-8", {
							"mx-8": !isMobileOnly,
							"mx-6 w-3/4": isMobileOnly,
						})}
					>
						{
							// The block is only visible if the partner is BC and if the current theme is BC
							// Ces blocs ne se modifient pas avec le "Modifier" global. Ils font appel à des API différentes
							isBCPartner && isBCCurrentTheme && (
								<>
									<BilingForm
										{...componentCommonProps}
										isEdit={true}
										isViewMode={isSellerPermission}
										isAdminEditing={false}
									/>
									{!isProductionEnvironment && (
										<>
											<hr className="h-1px bg-neutral400" />
											<MandateForm
												{...componentCommonProps}
												isEdit={true}
												isViewMode={
													isConnectedFromSupport || isSellerPermission
												}
												isAdminViewing={false}
											/>
										</>
									)}
								</>
							)
						}
					</div>
				</>
			)}
		</ProtectedLayout>
	)
}
